import { Form, Col, Select, Row } from 'antd'
import { Translate } from 'lang'
import { useEffect, useState } from 'react'
import { getCities, getCountries, getStates } from 'store/services/cscClient'

const style = {
  form: {
    display: 'inline-block',
    width: '100%',
  },
}
const defaultPlaceholder = label =>
  Translate({
    messageKey: 'inform',
    gender: 'male',
    items: {
      item: Translate({ messageKey: label, lower: true }),
    },
  })

function handleFilter(input, option) {
  return option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

export const LocalizationForm = ({ value, form, initialValues }) => {
  const { name, required, cols = { xs: 24 } } = value
  const [response, setResponse] = useState()
  const [data, setData] = useState(null)
  const [getInitial, setGetInitial] = useState(!!initialValues)
  const [ciso, setCiso] = useState()
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [statesLoading, setStatesLoading] = useState(false)
  const [citiesLoading, setCitiesLoading] = useState(false)

  useEffect(() => {
    if (!data?.type) return
    setResponse(prevState => ({
      ...prevState,
      [data.type]: data.payload,
    }))
    setData(null)
  }, [data])

  async function loadCountries() {
    setCountriesLoading(true)
    try {
      const countries = await getCountries()
      setData({ type: 'countries', payload: countries ?? [] })
    } catch (error) {
      setData({ type: 'countries', payload: [] })
    } finally {
      setCountriesLoading(false)
    }
  }

  async function loadStates(countryIso2) {
    if (!countryIso2) {
      setData({ type: 'state', payload: [] })
      return
    }
    setStatesLoading(true)
    try {
      const states = await getStates(countryIso2)
      setData({ type: 'state', payload: states ?? [] })
    } catch (error) {
      setData({ type: 'state', payload: [] })
    } finally {
      setStatesLoading(false)
    }
  }

  async function loadCities(countryIso2, stateIso2) {
    if (!countryIso2 || !stateIso2) {
      setData({ type: 'city', payload: [] })
      return
    }
    setCitiesLoading(true)
    try {
      const cities = await getCities(countryIso2, stateIso2)
      setData({ type: 'city', payload: cities ?? [] })
    } catch (error) {
      setData({ type: 'city', payload: [] })
    } finally {
      setCitiesLoading(false)
    }
  }

  useEffect(() => {
    loadCountries()
  }, [])

  useEffect(() => {
    const bootstrapInitialValues = async () => {
      if (!initialValues || !getInitial || !response?.countries) return
      const initialCountryIso2 = initialValues.country?.split('-')?.[0]
      const initialStateIso2 = initialValues.state?.split('-')?.[0]

      if (initialCountryIso2) {
        setCiso(initialCountryIso2)
        await loadStates(initialCountryIso2)
      }

      if (initialCountryIso2 && initialStateIso2) {
        await loadCities(initialCountryIso2, initialStateIso2)
      }

      setGetInitial(false)
    }

    bootstrapInitialValues()
  }, [initialValues, getInitial, response?.countries])

  const selects = [
    {
      name: [name, 'country'],
      label: defaultPlaceholder('country'),
      options: response?.countries,
      onChange: async value => {
        const countryIso2 = value.split('-')[0]
        setResponse(prevState => ({
          countries: prevState.countries,
          state: null,
          city: null,
        }))
        setCiso(countryIso2)
        await loadStates(countryIso2)
        form.setFieldsValue({ [name]: { city: null, state: null } })
      },
      loading: countriesLoading,
    },
    {
      name: [name, 'state'],
      label: defaultPlaceholder('state'),
      options: response?.state,
      onChange: async value => {
        const stateIso2 = value.split('-')[0]
        const selectedCountry = form.getFieldValue([name, 'country'])
        const countryIso2 = selectedCountry?.split('-')?.[0] ?? ciso
        setResponse(prevState => ({
          countries: prevState.countries,
          state: prevState.state,
          city: null,
        }))
        await loadCities(countryIso2, stateIso2)
        form.setFieldsValue({ [name]: { city: null } })
      },
      loading: statesLoading,
    },
    {
      name: [name, 'city'],
      label: defaultPlaceholder('city'),
      options: response?.city,
      onChange: null,
      loading: citiesLoading,
    },
  ]
  return (
    <Col {...cols}>
      <Row gutter={16}>
        {selects.map(s => {
          return (
            <Col xs={8} key={s.name}>
              <Form.Item
                name={s.name}
                style={style.form}
                label={s.label}
                hasFeedback
                rules={[{ required }]}
              >
                <Select
                  showSearch
                  filterOption={(input, option) => handleFilter(input, option)}
                  placeholder={s.label}
                  disabled={!s.options?.length}
                  loading={s.loading}
                  onChange={s.onChange}
                >
                  {s.options?.map(o => (
                    <Select.Option key={`key-${o.name}`} value={`${o.iso2 ?? o.id}-${o.name}`}>
                      {o.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )
        })}
      </Row>
    </Col>
  )
}
